'use client';

import { useRef, useState } from 'react';

import cx from 'classnames';

import { type Masked, unmask } from '~/contentful/graphql';
import HeadphoneOffIcon from '~/ui/assets/icons/headphone-off.svg';
import HeadphoneIcon from '~/ui/assets/icons/headphone.svg';
import { Button, ButtonSize, ButtonVariant } from '~/ui/components/button';
import { PlayPauseButton } from '~/ui/components/button/playPause';
import {
  type Header_AssetFragment,
  type Header_HeroAssetFragment,
} from '~/ui/modules/header/query';
import { type Hero_HeroAssetFragment } from '~/ui/modules/heroAsset/query';
import { Image, type ImageProps } from '~/ui/modules/image';
import { type Image_AssetFragment } from '~/ui/modules/image/query';
import spotlightStyles from '~/ui/modules/spotlight/spotlight.module.scss';

import styles from './heroAsset.module.scss';

export type HeroAssetType =
  | typeof Image_AssetFragment
  | typeof Header_AssetFragment
  | typeof Hero_HeroAssetFragment
  | typeof Header_HeroAssetFragment;

export interface HeroAssetProps {
  data: Masked<HeroAssetType>;
  videoWrapperClassName?: string;
  imageClassName?: string;
  imageProps?: Omit<ImageProps, 'className' | 'image'>;
}

export function HeroAsset({
  data,
  videoWrapperClassName,
  imageClassName,
  imageProps,
}: HeroAssetProps): React.ReactElement {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoPaused, setVideoPaused] = useState<boolean>(false);
  const [videoMuted, setVideoMuted] = useState<boolean>(true);

  const hero = unmask<HeroAssetType>(data);

  const image = hero
    ? 'image' in hero
      ? (hero.image as Masked<typeof Image_AssetFragment>)
      : (hero as Masked<typeof Image_AssetFragment>)
    : undefined;
  const videoUrl = 'video' in hero ? hero.video?.url : undefined;

  function toggleVideo() {
    setVideoPaused(!videoPaused);

    if (videoPaused) {
      videoRef?.current?.play();
    } else {
      videoRef?.current?.pause();
    }
  }
  function toggleMute() {
    setVideoMuted(!videoMuted);
  }

  const showMuteButton = 'videoHasAudio' in hero ? hero.videoHasAudio : false;

  return (
    <>
      {videoUrl ? (
        <div className={cx(videoWrapperClassName, styles.videoWrapper)}>
          <video
            className={cx(styles.video)}
            ref={videoRef}
            src={videoUrl}
            autoPlay
            controls={false}
            loop
            muted={videoMuted}
            playsInline
            disablePictureInPicture
            disableRemotePlayback
          />
          <div className={spotlightStyles.controls}>
            {showMuteButton && (
              <Button
                className={spotlightStyles.button}
                variant={ButtonVariant.SECONDARY_TRANSPARENT}
                size={ButtonSize.ICON}
                onClick={toggleMute}
              >
                {videoMuted ? (
                  <HeadphoneIcon className={spotlightStyles.iconMuteUnmute} aria-label="Unmute" />
                ) : (
                  <HeadphoneOffIcon className={spotlightStyles.iconMuteUnmute} aria-label="Mute" />
                )}
              </Button>
            )}

            <PlayPauseButton onClick={toggleVideo} isPaused={videoPaused} />
          </div>
        </div>
      ) : image ? (
        <Image className={cx(styles.image, imageClassName)} image={image} {...imageProps} />
      ) : null}
    </>
  );
}
